import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import naturalSort from 'javascript-natural-sort';
import styled from 'styled-components';

import { getValueFromInput } from 'util/FormsUtils';
import { Button, Checkbox, Col, FormControl, ListGroup, ListGroupItem, Row } from 'components/graylog';
import StoreProvider from 'injection/StoreProvider';

import type { Stream } from '../types';

const StyledP = styled.p`
  line-height: 20px;
  padding-top: 7px; 
`;
const StyledListGroupItem = styled(ListGroupItem)`
  padding-bottom: 7px;
  padding-top: 5px; 
`;
const StyledCol = styled(Col)`
  padding-left: 0; 
`;
const ScrollableListWrapper = styled.div`
  position: relative;

  .scrollable-list {
    max-height: 425px;
    overflow-y: auto;
  }

  .scrollable-list-header {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    display: block;
    z-index: 10;
  }

  .scrollable-list-item:nth-child(2) {
    margin-top: 40px;
  } 
`;
const StreamsStore = StoreProvider.getStore('Streams');

type Props = {
  excludedStreams: Array<string>,
  updateExcludedStreams: (streamsList: Array<string>)=>void,
};

const ArchiveConfigStreamSelectionForm = ({ excludedStreams, updateExcludedStreams }: Props) => {
  const [streams, setStreams] = useState<Array<Stream>>([]);
  const [filter, setFilter] = useState(undefined);

  useEffect(() => {
    StreamsStore.listStreams().then((streamsList: Array<Stream>) => {
      const sortedStreams = streamsList.sort((a, b) => naturalSort(a.title, b.title));

      setStreams(sortedStreams);
    });
  }, []);

  const onUpdateCheckbox = (streamId: string) => {
    return (e) => {
      // Clone the excludedStreams array.
      let newExcludes = excludedStreams.slice(0);

      if (e.target.checked) {
        if (newExcludes.indexOf(streamId) !== -1) {
          newExcludes = newExcludes.filter((id) => id !== streamId);
        }
      } else if (newExcludes.indexOf(streamId) === -1) {
        newExcludes.push(streamId);
      }

      updateExcludedStreams(newExcludes);
    };
  };

  const onSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newExcludes = [];

    if (!event.target.checked) {
      newExcludes = streams.map((stream) => stream.id);
    }

    updateExcludedStreams(newExcludes);
  };

  const onFilterChange = (event) => {
    setFilter(getValueFromInput(event.target));
  };

  const onFilterReset = () => {
    setFilter(undefined);
  };

  const isStreamIncluded = (stream: Stream) => {
    return excludedStreams && excludedStreams.indexOf(stream.id) === -1;
  };

  const filterListItem = () => {
    return (
      <Row>
        <Col xs={9} md={6}>
          <FormControl type="text" onChange={onFilterChange} placeholder="Filter streams" value={filter} />
        </Col>
        <StyledCol xs={3} md={2}>
          <Button onClick={onFilterReset}>Reset</Button>
        </StyledCol>
      </Row>
    );
  };

  const headerItem = () => {
    return (
      <StyledListGroupItem key="stream-select-all" className="scrollable-list-item scrollable-list-header">
        <Checkbox onChange={onSelectAll}>Select all available streams</Checkbox>
      </StyledListGroupItem>
    );
  };

  const streamItems = () => {
    const items = streams.filter((stream) => {
      const text = `${stream.title} ${stream.description}`;

      return !(filter && text.toLowerCase().indexOf(filter.toLowerCase()) === -1);
    }).map((stream) => {
      return (
        <ListGroupItem key={`stream-${stream.id}`} className="scrollable-list-item">
          <Checkbox checked={isStreamIncluded(stream)} onChange={onUpdateCheckbox(stream.id)}>
            <span><strong>{stream.title}</strong> - {stream.description}</span>
          </Checkbox>
        </ListGroupItem>
      );
    });

    if (items.length > 0) {
      return items;
    }

    return (
      <ListGroupItem className="scrollable-list-item">No streams match the filter.</ListGroupItem>
    );
  };

  if (!streams) {
    return (<StyledP>Loading streams...</StyledP>);
  }

  return (
    <div>
      {filterListItem()}
      <ScrollableListWrapper className="scrollable-list-container">
        <ListGroup className="scrollable-list">
          {headerItem()}
          {streamItems()}
        </ListGroup>
      </ScrollableListWrapper>
    </div>
  );
};

ArchiveConfigStreamSelectionForm.propTypes = {
  excludedStreams: PropTypes.arrayOf(PropTypes.string),
  updateExcludedStreams: PropTypes.func.isRequired,
};

ArchiveConfigStreamSelectionForm.defaultProps = {
  excludedStreams: [],
};

export default ArchiveConfigStreamSelectionForm;
